/**
 * DateRangePicker Component
 * V229: react-datepicker 라이브러리로 교체
 *
 * react-datepicker의 강력한 범위 선택 UI를 활용
 * - 시작일/종료일 명확한 시각적 구분
 * - 범위 중간 날짜 자동 하이라이트
 * - 한국어 로케일 지원
 * - Tailwind CSS와 통합
 */
import * as React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { Calendar as CalendarIcon, Check, X } from "lucide-react";
import {
  getDateRangeFromPreset,
  type QuickPreset,
} from "@/components/calendar";

// 한국어 로케일 등록
registerLocale("ko", ko);

// DateRange type - 기존 API 호환
export type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// react-datepicker 커스텀 스타일
import "react-datepicker/dist/react-datepicker.css";

interface DateRangePickerProps {
  /** 선택된 날짜 범위 */
  dateRange: DateRange | undefined;
  /** 날짜 범위 변경 핸들러 */
  onDateRangeChange: (range: DateRange | undefined) => void;
  /** 버튼 클래스명 */
  className?: string;
  /** 플레이스홀더 텍스트 */
  placeholder?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
  placeholder = "날짜 범위 선택",
  disabled = false,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  // 임시 선택 상태 (팝오버 내에서만 사용)
  const [startDate, setStartDate] = React.useState<Date | null>(
    dateRange?.from || null,
  );
  const [endDate, setEndDate] = React.useState<Date | null>(
    dateRange?.to || null,
  );

  // 팝오버 열릴 때 현재 값으로 초기화
  React.useEffect(() => {
    if (open) {
      setStartDate(dateRange?.from || null);
      setEndDate(dateRange?.to || null);
    }
  }, [open, dateRange]);

  const formatDateRange = () => {
    if (!dateRange?.from) return placeholder;

    if (dateRange.to) {
      return `${format(dateRange.from, "yyyy-MM-dd")} ~ ${format(dateRange.to, "yyyy-MM-dd")}`;
    }

    return format(dateRange.from, "yyyy-MM-dd");
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleConfirm = () => {
    if (startDate) {
      onDateRangeChange({
        from: startDate,
        to: endDate || startDate,
      });
    }
    setOpen(false);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
  };

  // V226: 프리셋 핸들러 - getDateRangeFromPreset 사용
  const handlePreset = (preset: string) => {
    const presetMap: Record<string, QuickPreset> = {
      today: "today",
      yesterday: "yesterday",
      last7days: "lastWeek",
      last30days: "lastMonth",
      thisMonth: "thisMonth",
      lastMonth: "last30Days",
    };

    const quickPreset = presetMap[preset] || "today";
    const range = getDateRangeFromPreset(quickPreset);

    if (range.from) {
      setStartDate(range.from);
      setEndDate(range.to || range.from);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !dateRange && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-background" align="start">
        <div className="flex flex-col sm:flex-row">
          {/* 프리셋 사이드바 */}
          <div className="flex flex-col gap-1 p-3 border-r border-border min-w-[140px]">
            <div className="text-xs font-medium text-muted-foreground mb-2 px-2">
              빠른 선택
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start font-normal h-8 px-2"
              onClick={() => handlePreset("today")}
            >
              오늘
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start font-normal h-8 px-2"
              onClick={() => handlePreset("yesterday")}
            >
              어제
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start font-normal h-8 px-2"
              onClick={() => handlePreset("last7days")}
            >
              최근 7일
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start font-normal h-8 px-2"
              onClick={() => handlePreset("last30days")}
            >
              최근 30일
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start font-normal h-8 px-2"
              onClick={() => handlePreset("thisMonth")}
            >
              이번 달
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start font-normal h-8 px-2"
              onClick={() => handlePreset("lastMonth")}
            >
              지난 달
            </Button>
          </div>

          <div className="p-3">
            {/* 선택된 범위 표시 헤더 */}
            <div className="flex items-center justify-between mb-4 bg-muted/30 p-3 rounded-md border border-border/50">
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                  Start Date
                </span>
                <span
                  className={cn(
                    "text-sm font-semibold",
                    !startDate && "text-muted-foreground",
                  )}
                >
                  {startDate ? format(startDate, "yyyy.MM.dd") : "시작일 선택"}
                </span>
              </div>
              <div className="h-8 w-[1px] bg-border mx-4" />
              <div className="flex items-center gap-2">
                <div className="flex flex-col text-right">
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                    End Date
                  </span>
                  <span
                    className={cn(
                      "text-sm font-semibold",
                      !endDate && "text-muted-foreground",
                    )}
                  >
                    {endDate ? format(endDate, "yyyy.MM.dd") : "종료일 선택"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setEndDate(new Date())}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  title="마감일을 오늘로 변경"
                >
                  오늘
                </button>
              </div>
            </div>

            {/* V229: react-datepicker 범위 선택 캘린더 */}
            <DatePicker
              selected={startDate}
              onChange={handleDateChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
              monthsShown={2}
              locale="ko"
              calendarStartDay={0}
              showPopperArrow={false}
              calendarClassName="!bg-background !border-0 !font-sans"
              dayClassName={(date) => {
                const isSelected =
                  startDate && endDate && date >= startDate && date <= endDate;
                const isStart =
                  startDate && date.getTime() === startDate.getTime();
                const isEnd = endDate && date.getTime() === endDate.getTime();

                if (isStart || isEnd) {
                  return "!bg-primary !text-primary-foreground !rounded-md hover:!bg-primary/90 !font-semibold";
                }
                if (isSelected) {
                  return "!bg-blue-100 dark:!bg-blue-900/40 !text-blue-900 dark:!text-blue-100 !rounded-none";
                }
                return "hover:!bg-accent !rounded-md";
              }}
              wrapperClassName="w-full"
              renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="flex items-center justify-between px-2 py-2">
                  <button
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    type="button"
                    className="p-1 hover:bg-accent rounded-md disabled:opacity-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="text-sm font-semibold">
                    {format(monthDate, "yyyy년 M월", { locale: ko })}
                  </span>
                  <button
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    type="button"
                    className="p-1 hover:bg-accent rounded-md disabled:opacity-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            />

            {/* 액션 버튼 */}
            <div className="flex justify-between items-center mt-3 pt-3 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="mr-1 h-3 w-3" />
                초기화
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                >
                  취소
                </Button>
                <Button size="sm" onClick={handleConfirm} disabled={!startDate}>
                  <Check className="mr-1 h-3 w-3" />
                  적용
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePicker;
